import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Project, UserProfile, SiteConfig, InboxMessage } from '../types';
import * as storage from '../services/storageService';

interface DataContextType {
  projects: Project[];
  profile: UserProfile | null;
  siteConfig: SiteConfig | null;
  inbox: InboxMessage[];
  loading: boolean;
  refreshData: () => Promise<void>;
  updateProjects: (projects: Project[]) => Promise<void>;
  updateProfile: (profile: UserProfile) => Promise<void>;
  updateSiteConfig: (config: SiteConfig) => Promise<void>;
  markMessageRead: (id: string) => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider = ({ children }: { children: ReactNode }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);
  const [inbox, setInbox] = useState<InboxMessage[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshData = async () => {
    try {
      setLoading(true);
      const [fetchedProjects, fetchedProfile, fetchedConfig, fetchedInbox] = await Promise.all([
        storage.getProjects(),
        storage.getProfile(),
        storage.getSiteConfig(),
        storage.getInboxMessages()
      ]);

      setProjects(fetchedProjects);
      setProfile(fetchedProfile);
      setSiteConfig(fetchedConfig);
      setInbox(fetchedInbox);
    } catch (error) {
      console.error("Failed to load data", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    refreshData();
  }, []);

  const updateProjects = async (newProjects: Project[]) => {
    // Optimistic update (update UI immediately)
    setProjects(newProjects);
    // Save to DB
    await storage.saveProjects(newProjects);
  };

  const updateProfile = async (newProfile: UserProfile) => {
    setProfile(newProfile);
    await storage.saveProfile(newProfile);
  };

  const updateSiteConfig = async (newConfig: SiteConfig) => {
    setSiteConfig(newConfig);
    await storage.saveSiteConfig(newConfig);
  };

  const markMessageRead = async (id: string) => {
    setInbox(prev => prev.map(msg => msg.id === id ? { ...msg, read: true } : msg));
    await storage.markMessageAsRead(id);
  };

  return (
    <DataContext.Provider value={{ 
      projects, 
      profile, 
      siteConfig, 
      inbox, 
      loading,
      refreshData,
      updateProjects,
      updateProfile,
      updateSiteConfig,
      markMessageRead
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
